import type { ReactElement } from 'react';
import { memo } from 'react';

import type { CheckboxProps } from '@mui/material/Checkbox';
import type { SxProps } from '@mui/material/styles';

import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';

interface SelectionCheckboxProps {
    /** Whether the checkbox renders at all; when false the component returns null. @default true. */
    shouldRender?: boolean;
    /** MUI checkbox size. @default 'small' */
    size?: CheckboxProps['size'];
    /** Whether the checkbox is disabled (also enables the tooltip when a message is set). */
    disabled?: boolean;
    /** Whether the checkbox is checked. */
    checked: boolean;
    /** Whether the checkbox shows the indeterminate (partial) state. @default false. */
    indeterminate?: boolean;
    /** Style overrides forwarded to the underlying MUI Checkbox. */
    sx?: SxProps;
    /** Called when the checkbox is toggled via click or Enter. */
    onToggle: () => void;
    /** Class name forwarded to the underlying MUI Checkbox. */
    className?: string;
    /** Tooltip text shown only while the checkbox is disabled. */
    tooltipMessage?: string | null;
}

const SelectionCheckbox = memo(
    ({
        shouldRender = true,
        size = 'small',
        disabled,
        checked,
        indeterminate = false,
        sx,
        onToggle,
        className,
        tooltipMessage,
    }: SelectionCheckboxProps): ReactElement | null => {
        if (!shouldRender) return null;

        const shouldDisableTooltip = !tooltipMessage || !disabled;

        const checkbox = (
            <Stack>
                <Checkbox
                    className={className}
                    size={size}
                    disabled={disabled}
                    checked={checked}
                    indeterminate={indeterminate}
                    sx={sx}
                    onClick={(e): void => {
                        e.stopPropagation();
                        onToggle();
                    }}
                    onKeyDown={(e): void => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                            onToggle();
                        }
                    }}
                />
            </Stack>
        );

        // A Tooltip that can never open still costs a component layer per checkbox, which adds up in large tables.
        if (shouldDisableTooltip) return checkbox;

        return (
            <Tooltip
                title={tooltipMessage}
                placement="top-start"
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [-2, 2], // Adjust the offset to position the tooltip correctly
                                },
                            },
                        ],
                    },
                }}
                leaveDelay={500}
            >
                {checkbox}
            </Tooltip>
        );
    }
);

SelectionCheckbox.displayName = 'SelectionCheckbox';

export { SelectionCheckbox };
export type { SelectionCheckboxProps };
